// Enum value lists. The core values match recursive-mls `src/support/model.ts`
// exactly (so ticketing/KB interoperate); values marked "staff ext" are additive
// extensions this staff platform needs and recursive-mls does not have.

// recursive-mls: new|open|pending|solved|closed. "hold" is a staff extension
// (waiting on internal, distinct from "pending" = waiting on requester).
// "spam" and "deleted" are HIDDEN statuses: excluded from every default queue,
// count, and the SLA sweep (they're not in OPEN_STATUSES), and reversible via a
// status change back to "open". "deleted" is a soft delete (recoverable), not a
// DB purge.
export const TICKET_STATUSES = ["new", "open", "pending", "hold", "solved", "closed", "spam", "deleted"] as const;
export const RESOLVED_STATUSES = ["solved", "closed"] as const;

export const TICKET_PRIORITIES = ["urgent", "high", "normal", "low"] as const;

// recursive-mls: form|email|phone|sms. chat|escalation|internal are staff ext.
export const TICKET_CHANNELS = ["form", "email", "phone", "sms", "chat", "escalation", "internal"] as const;

// recursive-mls models thread entries by direction (not an author enum).
export const MESSAGE_DIRECTIONS = ["inbound", "outbound"] as const;

export const KB_TYPES = ["video", "pdf", "article", "quiz"] as const;
export const KB_STATUSES = ["draft", "published"] as const;

export const CONFIG_STATUSES = ["unconfigured", "pending", "active", "error"] as const;
export const TENANT_STATUSES = ["provisioning", "active", "suspended", "error"] as const;

export const TONES = ["ok", "warn", "bad", "active", "neutral"] as const;
export const HEALTH = ["ok", "warn", "bad"] as const;

export const CSAT_SOURCES = ["ticket", "chat", "call", "project"] as const;
export const TIME_ENTRY_SOURCES = ["work_timer", "ticket_timer", "manual"] as const;

// ── Knowledge content + content studio (v3 — DATA_MODEL.md §1) ──────────────
// One store: a published help-centre article and a studio draft are the same row
// at different `state` values. Provenance is `origin` + `client_id`, never a flag.
export const BRAND_IDS = ["recursive", "solidearth", "nautilent"] as const;
export const ARTICLE_KINDS = ["article", "video"] as const;
export const ARTICLE_AUDIENCES = ["MLS admin", "Broker", "Agent", "Developer"] as const;
export const ARTICLE_PLANS = ["included", "addon"] as const;
// `platform` = ours, pushed down, read-only to clients; `tenant` = theirs, in
// their space, never overwritten by a push (DECISIONS.md §1). The two are
// separate values — not a nullable client_id — because that difference decides
// who may edit and who may read.
export const CONTENT_ORIGINS = ["platform", "tenant"] as const;
export const ARTICLE_STATES = [
  "suggested",
  "drafted",
  "review",
  "scheduled",
  "published",
  "superseded",
] as const;

// A content_proposal is a unit of work the studio proposes. `retire` requires a
// named successor before it can go (DECISIONS.md §8, policy row).
export const PROPOSAL_KINDS = ["new", "update", "video", "retire"] as const;
export const PROPOSAL_STATES = ["suggested", "drafted", "review", "scheduled"] as const;
// The six watched sources (V3_README §C). search_miss and failed_deflection
// depend on portal telemetry (DECISIONS.md Q7).
export const CONTENT_SOURCES = [
  "release_notes",
  "schema_change",
  "ticket_cluster",
  "search_miss",
  "failed_deflection",
  "config_drift",
] as const;
export const PROPOSAL_RESOLUTIONS = ["published", "rejected", "superseded"] as const;

// Video pipeline (V3_README §9 / DECISIONS.md Q6). A video proposal carries media
// production rows; each row has a REAL state (not a free-text note), so the studio
// can gate publish on human review and the pipeline can drive transitions.
export const MEDIA_KINDS = ["screenshot", "video", "storyboard", "voiceover", "captions"] as const;
// not_proposed = deliberately not doing this artifact (e.g. "topic is under 3
// minutes — article only"); pending = queued to produce; draft = produced but
// UNREVIEWED (a synthesised voiceover a human must still listen to); ready =
// human-cleared; needs_rerecord = a change invalidated it (e.g. an affected video
// chapter). A video proposal may publish only when NO row is pending/draft/
// needs_rerecord (DECISIONS.md §8 — "a person watches it first").
export const MEDIA_STATES = ["not_proposed", "pending", "draft", "ready", "needs_rerecord"] as const;

// How a RealtyID ↔ Displet link was verified (DATA_MODEL.md §5). The RID is the
// permanent key that follows a person between brokerages; the Displet user_id
// does not.
export const REALTY_ID_VERIFY_VIA = ["otp_email", "otp_sms", "sso", "staff"] as const;

// Project membership roles (DATA_MODEL.md §4). Client-side: viewer|contributor|
// sponsor; our side: pm|lead|member. A member row carries exactly one side.
export const CLIENT_PROJECT_ROLES = ["viewer", "contributor", "sponsor"] as const;
export const OUR_PROJECT_ROLES = ["pm", "lead", "member"] as const;
export const PROJECT_MEMBER_ROLES = [...OUR_PROJECT_ROLES, ...CLIENT_PROJECT_ROLES] as const;

// The four portal sign-in credential paths, in presentation order. RealtyID is
// first on purpose — it is permanent and trains the durable habit (DECISIONS.md
// §2). No API-key sign-in: a human signing in with a server credential is a
// category error.
export const PORTAL_AUTH_METHODS = ["rid", "passkey", "pw", "sso"] as const;

/**
 * Categories that never auto-publish, whatever the diff. Kept as data, not baked
 * into `autoPublishable`, so ops can add a category without a deploy
 * (DATA_MODEL.md §1.3). Seed default; the live list lives in feature config.
 */
export const DEFAULT_PROTECTED_CATEGORIES = [
  "Contracts",
  "Billing",
  "Licence terms",
] as const;

export const AUDIT_TARGET_KINDS = [
  "ticket",
  "person",
  "project",
  "tenant",
  "invoice",
  "announcement",
  "member",
  "mailbox",
  "merge",
  "export",
  "article",
] as const;

/**
 * Derive the human-facing ticket ref (`TCK-####`) from a `tck_<rand>` key, using
 * the same algorithm as recursive-mls `ticketRef()` — last 6 hex chars →
 * parseInt(16) % 100000 → zero-padded. The ref is derived, never stored.
 */
export function ticketRef(ticketKey: string): string {
  const hex = ticketKey.slice(-6);
  const parsed = parseInt(hex, 16);
  const num = Number.isFinite(parsed) ? parsed % 100000 : 0;
  return `TCK-${String(num).padStart(4, "0")}`;
}
